<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useUserStore } from '../useUserStore';
import { storeToRefs } from 'pinia';
import MultipleSelectInput from '@/corePagesModule/SelectInput/MultipleSelectInput.vue';
import { i18n } from '@/i18n';
import { FilterInfoState } from '@/corePagesModule/SelectInput/interface/uiState';
import { isNone, isSuccess } from '@/coreDomainsModule/uiLoadingState';
import { UserAccount } from '@/coreDomainsModule/entities/users';
import { UserAccountService } from '@/coreDomainsModule/services/userAccountService';
const t = i18n.t;
const store = useUserStore();
const { state } = storeToRefs(store);
const { $reset, init, fetchUserList } = store;
const props = withDefaults(
  defineProps<{
    selectInputIndex: number;
    label?: string;
    placeholder?: string;
    originalSelectedUserIdList?: string[];
    selectedListMaxHeight?: number;
  }>(),
  { label: undefined, placeholder: undefined, originalSelectedUserIdList: undefined, selectedListMaxHeight: undefined }
);
const emits = defineEmits<{ (e: 'update:modelValue', value: string[]); (e: 'update:modelValueUserAccounts', value: UserAccount[]) }>();
const didOpen = ref(false);
/**
 * fetchの状態を変更する関数
 * SelectInputを開いたり閉じたりするとstateがリセットされてしまうので、ここで書き換えて更新をかけている
 */
const switchFetchState = () => {
  state.value.searchingUserList = state.value.userList;
  if (state.value.userList.length > 0 && didOpen.value) {
    if (isSuccess(state.value.userListFetchState.status)) {
      state.value.userListFetchState.status = { type: 'none' };
    } else if (isNone(state.value.userListFetchState.status)) {
      state.value.userListFetchState.status = { type: 'success' };
    }
  } else {
    didOpen.value = true;
  }
};
onMounted(async () => {
  await init();
  nextTick(() => {
    state.value.selectedUserList = confirmedOriginalSelectedUserIdList.value;
  });
});
onBeforeUnmount(() => {
  $reset();
});
watch(
  () => props.originalSelectedUserIdList,
  () => {
    nextTick(() => {
      state.value.selectedUserList = confirmedOriginalSelectedUserIdList.value;
    });
  }
);
const userNameList = computed(() => state.value.userList.map((user) => UserAccountService.getDisplayName(user) ?? ''));
const searchedUserNameList = computed(() => state.value.searchingUserList.map((user) => UserAccountService.getDisplayName(user) ?? ''));
const selectedUserNameList = computed(() => state.value.selectedUserList.map((user) => UserAccountService.getDisplayName(user) ?? ''));
const label = computed(() => props.label ?? t('担当者'));
const placeholder = computed(() => props.placeholder ?? t('担当者'));
const selectedIndexes = computed((): number[] => {
  return userNameList.value.reduce((acc, userName, index) => {
    if (selectedUserNameList.value.includes(userName)) {
      acc.push(index);
    }
    return acc;
  }, [] as number[]);
});
const confirmedOriginalSelectedUserIdList = computed<UserAccount[]>(() => {
  if (!props.originalSelectedUserIdList || !(props.originalSelectedUserIdList.length > 0)) return [];
  return props.originalSelectedUserIdList
    .map((userId) => {
      const userData = state.value.userList.find((user) => user.email === userId);
      if (!userData) {
        console.error('ユーザーが見つかりません', userId);
        return undefined;
      }
      return userData;
    })
    .filter((userData) => userData !== undefined);
});
/**
 * SelectInputの値が更新された際のハンドラ
 * @param value - 選択されたユーザー名リスト
 */
const onUpdate = (value: string[]) => {
  const selectedUserList: UserAccount[] = state.value.userList.filter((user) => value.includes(UserAccountService.getDisplayName(user)));
  const selectedUserIdList = selectedUserList.map((user) => {
    if (user.email === undefined) {
      console.error('IDのないユーザー情報が存在します', user);
      throw new Error('IDのないユーザー情報が存在します');
    }
    return user.email;
  });
  state.value.selectedUserList = selectedUserList;
  emits('update:modelValue', selectedUserIdList);
  emits('update:modelValueUserAccounts', selectedUserList);
};
/**
 * キーワード
 * @param keyword - キーワード
 */
const onSearch = async (keyword: string) => {
  await fetchUserList(keyword);
};
</script>
<template>
  <MultipleSelectInput
    :index="selectInputIndex"
    :selected-list="selectedUserNameList"
    :placeholder="placeholder"
    :label="label"
    filter-type="USER"
    :prop-options="userNameList"
    :prop-filtered-options="searchedUserNameList"
    :prop-paging-state="(state.userListFetchState.status as FilterInfoState)"
    :prop-selected-indexes="selectedIndexes"
    :use-reset="false"
    use-teleport
    :selected-list-max-height="selectedListMaxHeight"
    @click:select-input="switchFetchState"
    @update:model-value="onUpdate"
    @close="switchFetchState"
    @search="onSearch"
  />
</template>
<style lang="scss" scoped></style>
